<template>
  <NavBar />
  <FooterView class="footer-view" />

  <div v-if="isLoading" class="loading-mask">
    <div class="app-loader-card">
      <span class="app-loader-mark"></span>
      <span class="loading-text">
        加载中
        <span class="app-loader-dots"><span></span><span></span><span></span></span>
      </span>
    </div>
  </div>

  <ThemeToggle :theme="theme" @toggle="toggleTheme" />

  <router-view />
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import NavBar from './components/NavBar.vue';
import FooterView from './components/FooterView.vue';
import ThemeToggle from './components/ThemeToggle.vue';
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.min.js";

export default {
  components: {
    NavBar,
    FooterView,
    ThemeToggle,
  },
  setup() {
    const router = useRouter();
    const isLoading = ref(true);
    const theme = ref(localStorage.getItem('app_theme') || 'light');
    let loadingTimer = null;

    const hideLoading = () => {
      isLoading.value = false;
      if (loadingTimer) {
        clearTimeout(loadingTimer);
        loadingTimer = null;
      }
    };

    const scheduleHide = (delay = 200) => {
      if (loadingTimer) clearTimeout(loadingTimer);
      loadingTimer = setTimeout(hideLoading, delay);
    };

    const applyTheme = () => {
      document.documentElement.setAttribute('data-theme', theme.value);
      document.body.setAttribute('data-theme', theme.value);
      localStorage.setItem('app_theme', theme.value);
    };

    const toggleTheme = () => {
      theme.value = theme.value === 'light' ? 'dark' : 'light';
    };

    const cleanupModalState = () => {
      if (document.querySelector('.modal.show')) return;
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
      document.querySelectorAll('.modal-backdrop').forEach(backdrop => backdrop.remove());
    };

    watch(theme, applyTheme, { immediate: true });

    const removeBefore = router.beforeEach((to, from, next) => {
      isLoading.value = true;
      scheduleHide(2000);
      next();
    });

    router.afterEach(() => {
      scheduleHide(200);
    });

    router.onError(() => {
      scheduleHide(100);
    });

    onMounted(() => {
      document.addEventListener('hidden.bs.modal', cleanupModalState);
      router.isReady().then(() => scheduleHide(200));
    });

    onUnmounted(() => {
      if (typeof removeBefore === 'function') {
        removeBefore();
      }
      if (loadingTimer) clearTimeout(loadingTimer);
      document.removeEventListener('hidden.bs.modal', cleanupModalState);
    });

    return {
      isLoading,
      theme,
      toggleTheme,
    };
  }
}
</script>

<style>
:root {
  color-scheme: light;
  --app-bg:
    linear-gradient(135deg, rgba(34, 197, 94, 0.12), transparent 34%),
    linear-gradient(225deg, rgba(124, 58, 237, 0.12), transparent 38%),
    linear-gradient(180deg, #f8fafc 0%, #eef6f4 48%, #f6f5ff 100%);
  --app-surface: rgba(255, 255, 255, 0.86);
  --app-surface-strong: rgba(255, 255, 255, 0.96);
  --app-surface-muted: rgba(15, 23, 42, 0.04);
  --app-text: #10202f;
  --app-muted: #536273;
  --app-border: rgba(15, 23, 42, 0.12);
  --app-primary: #2563eb;
  --app-primary-strong: #1d4ed8;
  --app-accent: #14b8a6;
  --app-success: #16a34a;
  --app-warning: #f59e0b;
  --app-danger: #dc2626;
  --app-placeholder: rgba(15, 32, 47, 0.54);
  --app-shadow: 0 18px 48px rgba(20, 31, 49, 0.14);
  --app-shadow-soft: 0 10px 24px rgba(20, 31, 49, 0.1);
  --nav-icon-bg: rgba(15, 23, 42, 0.06);
  --nav-icon-filter: brightness(0) saturate(100%) invert(18%) sepia(21%) saturate(987%) hue-rotate(169deg) brightness(93%) contrast(92%);
  --nav-icon-active-bg: rgba(37, 99, 235, 0.12);
  --nav-icon-active-filter: brightness(0) saturate(100%) invert(42%) sepia(91%) saturate(749%) hue-rotate(190deg) brightness(93%) contrast(94%);
  --app-radius: 10px;
  --app-radius-lg: 14px;
}

:root[data-theme="dark"],
:root[data-theme="dark"] html,
:root[data-theme="dark"] body,
:root[data-theme="dark"] #app,
html[data-theme="dark"],
body[data-theme="dark"] {
  color-scheme: dark;
  --app-bg:
    #050505;
  --app-surface: rgba(17, 25, 39, 0.84);
  --app-surface-strong: rgba(20, 29, 45, 0.96);
  --app-surface-muted: rgba(255, 255, 255, 0.06);
  --app-text: #edf5f8;
  --app-muted: #a8b6c4;
  --app-border: rgba(255, 255, 255, 0.13);
  --app-primary: #60a5fa;
  --app-primary-strong: #3b82f6;
  --app-accent: #2dd4bf;
  --app-success: #22c55e;
  --app-warning: #fbbf24;
  --app-danger: #f87171;
  --app-placeholder: rgba(237, 245, 248, 0.56);
  --app-shadow: 0 22px 56px rgba(0, 0, 0, 0.42);
  --app-shadow-soft: 0 12px 30px rgba(0, 0, 0, 0.28);
  --nav-icon-bg: rgba(255, 255, 255, 0.1);
  --nav-icon-filter: brightness(0) saturate(100%) invert(96%) sepia(6%) saturate(480%) hue-rotate(168deg) brightness(102%) contrast(95%);
  --nav-icon-active-bg: rgba(96, 165, 250, 0.18);
  --nav-icon-active-filter: brightness(0) saturate(100%) invert(75%) sepia(56%) saturate(888%) hue-rotate(176deg) brightness(101%) contrast(96%);
}

* {
  box-sizing: border-box;
}

html {
  min-height: 100%;
  margin: 0;
  padding: 0;
  background: var(--app-bg);
}

body {
  min-height: 100%;
  margin: 0;
  padding: 0;
  background: var(--app-bg);
  background-attachment: fixed;
  color: var(--app-text);
  font-family: "Fira Sans", "Microsoft YaHei", "PingFang SC", "Segoe UI", Arial, sans-serif;
  transition: background 0.35s ease, color 0.25s ease;
}

body::before {
  content: "";
  position: fixed;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
  background-size: 36px 36px;
  mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.5), transparent 78%);
  z-index: -1;
}

:root[data-theme="dark"] body::before,
body[data-theme="dark"]::before {
  background-image: none;
  display: none;
}

:root[data-theme="dark"],
:root[data-theme="dark"] html,
:root[data-theme="dark"] body,
:root[data-theme="dark"] #app,
html[data-theme="dark"],
body[data-theme="dark"],
body[data-theme="dark"] #app {
  background: #050505 !important;
}

#app {
  min-height: 100vh;
  margin: 0;
  padding-top: 0;
  color: var(--app-text);
  padding-bottom: 86px;
}

a {
  color: var(--app-primary);
}

a:hover {
  color: var(--app-accent);
}

.container {
  max-width: 1180px;
}

.footer-view {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1000;
}

.loading-mask {
  position: fixed;
  inset: 0;
  background:
    radial-gradient(circle at 50% 42%, rgba(96, 165, 250, 0.22), transparent 26%),
    radial-gradient(circle at 22% 28%, rgba(45, 212, 191, 0.14), transparent 22%),
    radial-gradient(circle at 78% 70%, rgba(124, 58, 237, 0.14), transparent 24%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.035), transparent 42%),
    rgba(5, 5, 5, 0.9);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  z-index: 2000;
}

.loading-mask::before {
  content: "";
  position: absolute;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: conic-gradient(from 90deg, rgba(45, 212, 191, 0), rgba(45, 212, 191, 0.72), rgba(96, 165, 250, 0));
  filter: blur(22px);
  opacity: 0.5;
  animation: app-loader-spin 2.4s linear infinite;
}

.loading-mask::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    linear-gradient(115deg, transparent 0%, transparent 42%, rgba(255, 255, 255, 0.08) 50%, transparent 58%, transparent 100%);
  background-size: 42px 42px, 42px 42px, 220% 220%;
  mask-image: radial-gradient(circle at center, rgba(0, 0, 0, 0.8), transparent 72%);
  opacity: 0.55;
  animation: app-loader-sheen 3.8s ease-in-out infinite;
}

.app-loader-card {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 14px;
  min-height: 58px;
  padding: 14px 20px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.78);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.38);
  backdrop-filter: blur(18px);
}

.app-loader-mark {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22c55e, #60a5fa);
  box-shadow: 0 0 24px rgba(45, 212, 191, 0.42);
  animation: app-loader-pulse 1.35s ease-in-out infinite;
}

.loading-text {
  color: #f8fafc;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0;
  text-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}

.app-loader-dots {
  display: inline-flex;
  gap: 4px;
  margin-left: 2px;
  vertical-align: middle;
}

.app-loader-dots span {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #93c5fd;
  animation: app-loader-dot 1s ease-in-out infinite;
}

.app-loader-dots span:nth-child(2) {
  animation-delay: 0.14s;
}

.app-loader-dots span:nth-child(3) {
  animation-delay: 0.28s;
}

@keyframes app-loader-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes app-loader-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.84;
  }

  50% {
    transform: scale(1.12);
    opacity: 1;
  }
}

@keyframes app-loader-dot {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.45;
  }

  40% {
    transform: translateY(-5px);
    opacity: 1;
  }
}

@keyframes app-loader-sheen {
  0%,
  100% {
    background-position: 0 0, 0 0, 120% 0;
  }

  50% {
    background-position: 0 0, 0 0, -20% 100%;
  }
}

.card,
.modal-content,
.dropdown-menu,
.offcanvas {
  background: var(--app-surface);
  color: var(--app-text);
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-lg);
  box-shadow: var(--app-shadow);
  backdrop-filter: blur(18px);
}

.modal {
  z-index: 3100 !important;
}

.modal-backdrop {
  z-index: 3000 !important;
}

.modal-dialog {
  color: var(--app-text);
}

.modal-header,
.modal-footer {
  border-color: var(--app-border);
}

.card {
  overflow: hidden;
}

.card-header,
.card-footer {
  background: var(--app-surface-muted);
  color: var(--app-text);
  border-color: var(--app-border);
}

.list-group-item,
.dropdown-item {
  background: transparent;
  color: var(--app-text);
}

.dropdown-item:hover,
.dropdown-item:focus {
  background: var(--app-surface-muted);
  color: var(--app-primary);
}

.dropdown-divider,
hr {
  border-color: var(--app-border);
  opacity: 1;
}

.btn {
  border-radius: 9px;
  font-weight: 600;
  letter-spacing: 0;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease, border-color 0.18s ease;
}

.btn:hover,
.btn:focus-visible {
  transform: translateY(-1px);
}

.btn-primary {
  background: var(--app-primary);
  border-color: var(--app-primary);
  box-shadow: 0 12px 24px rgba(37, 99, 235, 0.22);
}

.btn-primary:hover,
.btn-primary:focus-visible {
  background: var(--app-primary-strong);
  border-color: var(--app-primary-strong);
}

.btn-success {
  background: var(--app-success);
  border-color: var(--app-success);
}

.btn-warning {
  background: var(--app-warning);
  border-color: var(--app-warning);
  color: #172033;
}

.btn-danger {
  background: var(--app-danger);
  border-color: var(--app-danger);
}

.btn-secondary,
.btn-outline-secondary,
.btn-outline-success,
.btn-outline-primary {
  background: var(--app-surface-muted);
  color: var(--app-text);
  border-color: var(--app-border);
}

.btn-secondary:hover,
.btn-outline-secondary:hover,
.btn-outline-success:hover,
.btn-outline-primary:hover {
  background: rgba(20, 184, 166, 0.14);
  color: var(--app-text);
  border-color: var(--app-accent);
}

.form-control,
.form-select,
.input-group-text {
  background: var(--app-surface-strong);
  color: var(--app-text);
  border-color: var(--app-border);
  border-radius: 9px;
}

.input-group .form-control,
.input-group .input-group-text {
  border-radius: 0;
}

.form-control:focus,
.form-select:focus {
  background: var(--app-surface-strong);
  color: var(--app-text);
  border-color: var(--app-accent);
  box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.2);
}

.form-control::placeholder {
  color: var(--app-placeholder);
}

.form-label,
.text-muted {
  color: var(--app-muted) !important;
}

.alert {
  border-radius: var(--app-radius);
  border-color: var(--app-border);
  box-shadow: var(--app-shadow-soft);
}

.table {
  color: var(--app-text);
}

.themed-table {
  width: 100%;
  background: var(--app-surface) !important;
  color: var(--app-text) !important;
  border-radius: var(--app-radius-lg);
  overflow: hidden;
  box-shadow: var(--app-shadow);
}

.themed-table thead,
.table thead {
  background: var(--app-surface-muted);
}

.themed-table thead th {
  color: var(--app-text) !important;
  border-color: var(--app-border) !important;
  background: var(--app-surface-muted) !important;
}

.themed-table th,
.themed-table td {
  vertical-align: middle;
  border-color: var(--app-border) !important;
}

.themed-table.table-bordered>:not(caption)>*>* {
  border-color: var(--app-border) !important;
}

.themed-table tbody tr:nth-of-type(odd)>*,
.table-striped>tbody>tr:nth-of-type(odd)>* {
  background-color: rgba(255, 255, 255, 0.04) !important;
  color: var(--app-text) !important;
}

.themed-table tbody tr:nth-of-type(even)>*,
.table-striped>tbody>tr:nth-of-type(even)>* {
  background-color: rgba(255, 255, 255, 0.075) !important;
  color: var(--app-text) !important;
}

:root[data-theme="light"] .themed-table tbody tr:nth-of-type(odd)>*,
:root[data-theme="light"] .table-striped>tbody>tr:nth-of-type(odd)>*,
body[data-theme="light"] .themed-table tbody tr:nth-of-type(odd)>*,
body[data-theme="light"] .table-striped>tbody>tr:nth-of-type(odd)>* {
  background-color: rgba(15, 23, 42, 0.025) !important;
}

:root[data-theme="light"] .themed-table tbody tr:nth-of-type(even)>*,
:root[data-theme="light"] .table-striped>tbody>tr:nth-of-type(even)>*,
body[data-theme="light"] .themed-table tbody tr:nth-of-type(even)>*,
body[data-theme="light"] .table-striped>tbody>tr:nth-of-type(even)>* {
  background-color: rgba(20, 184, 166, 0.055) !important;
}

.themed-table tbody tr:hover>*,
.table-hover tbody tr:hover>* {
  background-color: rgba(20, 184, 166, 0.14) !important;
  color: var(--app-text) !important;
}

.themed-pagination .page-link,
.pagination .page-link {
  background: var(--app-surface);
  color: var(--app-text);
  border-color: var(--app-border);
}

.themed-pagination .page-link:hover,
.pagination .page-link:hover {
  background: rgba(20, 184, 166, 0.14);
  color: var(--app-text);
}

.themed-pagination .active .page-link,
.pagination .active .page-link {
  background: var(--app-primary);
  border-color: var(--app-primary);
  color: #fff;
}

img {
  max-width: 100%;
}

button,
[role="button"],
.nav-link,
.dropdown-item,
.card[onclick] {
  cursor: pointer;
}

@media (max-width: 768px) {
  #app {
    padding-bottom: 104px;
  }

  .app-loader-card {
    padding: 12px 16px;
  }

  .loading-text {
    font-size: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}
</style>
